import React, { useState, useRef, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "../../assets/HrScreeningReport.css";
import "../../assets/AiCallingReport.css";
import { useParams } from "react-router-dom";
// Import commented out since we're using static data
import { get_ai_calling_report_by_id } from "../../services/psychometric_report.service";

const safeText = (value, fallback = "N/A") =>
  value !== undefined && value !== null && value !== "" ? value : fallback;

const AiCallingReport = () => {
  const { apct_id } = useParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(185);
  const [activeSection, setActiveSection] = useState("summary");
  const audioRef = useRef(null);

  const [candidate, setCandidate] = useState({});
  const [audioUrl, setAudioUrl] = useState("");
  const [transcript, setTranscript] = useState([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isReportGenerating, setIsReportGenerating] = useState(false);
  // Flag to track if using static data as fallback
  const [usingStaticData, setUsingStaticData] = useState(false);

  // Static data for fallback if API call fails
  const staticCandidateData = {
    name: "Jordan Smith",
    email: "jordan.smith@example.com",
    phone: "+91 9876543210",
    position: "Senior Frontend Developer",
    status: "qualified",
    interviewDate: "April 8, 2025",
    screeningScore: 87,
    screeningDetails: {
      totalExperience: "5 years 3 months",
      relevantExperience: "4 years 6 months",
      currentCompany: "TechCraft Solutions",
      currentRole: "Frontend Developer",
      noticePeriod: "2 months",
      expectedSalary: "$90,000 per annum",
      willingToRelocate: "Yes",
      remoteWorkPreference: "Hybrid (3 days remote, 2 days office)",
      reasonForChange:
        "Career growth and opportunity to work with newer technologies",
    },
    technicalSkills: [
      { name: "React", score: 90 },
      { name: "JavaScript", score: 85 },
      { name: "TypeScript", score: 75 },
      { name: "HTML/CSS", score: 88 },
      { name: "Redux", score: 70 },
    ],
    educationDetails: {
      highestDegree: "Bachelor of Technology",
      specialization: "Computer Science",
      university: "ABC University",
      graduationYear: "2018",
    },
    availabilityDetails: {
      interviewAvailability: "Weekdays after 6 PM",
      startDate: "June 15, 2025",
      relocateConstraints: "None",
    },
    recommendation:
      "The candidate has strong relevant experience in frontend development with the required technical skills. Their notice period is reasonable and salary expectations align with our range. Recommend advancing to the technical round.",
    redFlags: [
      "Frequent job changes in the past (3 companies in 5 years)",
      "Some gaps in TypeScript knowledge",
    ],
    flagDistribution: [
      { name: "Green", value: 75 },
      { name: "Amber", value: 20 },
      { name: "Red", value: 5 },
    ],
  };

  const staticTranscriptData = [
    {
      question:
        "Could you please introduce yourself and tell me about your total experience in the industry?",
      answer:
        "Hello, I'm Jordan Smith. I have a total of 5 years and 3 months of experience in web development. I started my career at WebTech Inc. as a junior developer and then moved to my current role at TechCraft Solutions where I've been working as a Frontend Developer for the past 2 years.",
    },
    {
      question:
        "How much of your experience is specifically relevant to the Frontend Developer role you're applying for?",
      answer:
        "Out of my total experience, I have about 4 years and 6 months of focused frontend development experience. I've been working extensively with React, JavaScript, and related frontend technologies during this time.",
    },
    {
      question:
        "What are your primary technical skills? Could you rate your proficiency in each?",
      answer:
        "My strongest skills are in React and JavaScript, where I'd rate myself about 9/10. I'm also proficient in HTML/CSS at around 8.5/10. I've been working with TypeScript for about 2 years now, so I'd rate myself 7.5/10 there. I also have experience with Redux, which I'd put at about 7/10.",
    },
    {
      question: "What is your current notice period at your present company?",
      answer:
        "My current notice period is 2 months as per my contract with TechCraft Solutions.",
    },
    {
      question: "What are your salary expectations for this role?",
      answer:
        "Based on my experience and the market rates, I'm expecting around $90,000 per annum. However, I'm open to discussion based on the complete benefits package.",
    },
    {
      question: "Are you willing to relocate for this position if needed?",
      answer:
        "Yes, I am willing to relocate. I don't have any constraints that would prevent me from moving to a new location for this opportunity.",
    },
    {
      question: "What is your preference regarding remote work?",
      answer:
        "I prefer a hybrid working model, ideally with 3 days working remotely and 2 days in the office. I find this balance gives me both the flexibility of working from home and the collaboration benefits of in-office interactions.",
    },
    {
      question: "When would you be available to start if selected?",
      answer:
        "After serving my notice period of 2 months, I could start around June 15th, 2025.",
    },
  ];

  // Function to use static data as fallback
  const useStaticDataFallback = () => {
    console.log("Using static HR screening data as fallback");
    setAudioUrl(""); // No actual audio in static data
    setTranscript(staticTranscriptData);
    setCandidate(staticCandidateData);
    setIsReportGenerating(false);
    setUsingStaticData(true);
    setIsLoading(false);
  };

  // Function to fetch static data (replaces API call)
  const fetchCandidateData = async () => {
    setIsLoading(true);
    try {
      const response = await get_ai_calling_report_by_id(apct_id);
      if (response.status === 200) {
        const reportData = response.data?.report?.interview_report_data;
        const audio_Url = response.data?.report?.audio_url;
        const transcript = response.data?.report?.transcript;
        setAudioUrl(audio_Url);
        setTranscript(transcript);
        setCandidate(reportData);
        if (!reportData || Object.keys(reportData).length === 0 || !reportData.screeningDetails) {
          setIsReportGenerating(true);
        } else {
          setIsReportGenerating(false);
        }
        setUsingStaticData(false);
      }
    } catch (err) {
      console.error("Error fetching candidate data:", err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        setIsReportGenerating(false);
      }, 500);
    }
  };

  useEffect(() => {
    fetchCandidateData();
  }, [apct_id]);

  // Render loading or generating message if needed
  if (isLoading) {
    return (
      <div className="hr-report-loading-container">
        <div className="hr-report-loader"></div>
        <h3>Loading candidate screening data</h3>
        <p>Please wait while we retrieve the HR screening information...</p>
      </div>
    );
  }

  if (isReportGenerating) {
    return (
      <div className="ai-report-generating-container">
        <div className="ai-report-card">
          <div className="ai-report-header">
            <h1 className="ai-report-header-title">
              AI Interview Analysis Report
            </h1>
          </div>
          <div className="ai-report-generating-message">
            <div className="ai-report-loader"></div>
            <h2>Your report is being generated</h2>
            <p>
              Our AI system is analyzing the interview data. This typically
              takes a few minutes to complete.
            </p>
            <div className="ai-report-status-info">
              <span className="ai-report-status-dot"></span>
              <span>Report status: Processing</span>
            </div>
            <button
              className="ai-report-refresh-button"
              onClick={fetchCandidateData}
            >
              <span className="ai-report-small-loader"></span>
              Check Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!candidate || !candidate.screeningScore) {
    return (
      <div className="ai-report-error-container">
        <div className="ai-report-card">
          <div className="ai-report-header">
            <h1 className="ai-report-header-title">
              AI Interview Analysis Report
            </h1>
          </div>
          <div className="ai-report-error-message">
            <div className="ai-report-error-icon">
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="#d32f2f"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 8V12"
                  stroke="#d32f2f"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="16" r="1" fill="#d32f2f" />
              </svg>
            </div>
            <h2>Unable to load report data</h2>
            <p>
              There was a problem loading the report data. This could be due to
              the report still being processed or a technical issue.
            </p>
            <div className="ai-report-error-actions">
              <button
                className="ai-report-refresh-button"
                onClick={fetchCandidateData}
              >
                Try Again
              </button>
              <button
                className="ai-report-secondary-button"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For demonstration/development, create mock data if none exists
  // In production, this would be removed and proper error handling would be in place
  // if (!candidate || !candidate.screeningDetails) {
  //   // Mock data structure
  //   const mockCandidate = {
  //     name: "Jordan Smith",
  //     email: "jordan.smith@example.com",
  //     phone: "+91 9876543210",
  //     position: "Senior Frontend Developer",
  //     status: "qualified",
  //     interviewDate: "April 8, 2025",
  //     screeningScore: 87,
  //     screeningDetails: {
  //       totalExperience: "5 years 3 months",
  //       relevantExperience: "4 years 6 months",
  //       currentCompany: "TechCraft Solutions",
  //       currentRole: "Frontend Developer",
  //       noticePeriod: "2 months",
  //       expectedSalary: "$90,000 per annum",
  //       willingToRelocate: "Yes",
  //       remoteWorkPreference: "Hybrid (3 days remote, 2 days office)",
  //       reasonForChange: "Career growth and opportunity to work with newer technologies"
  //     },
  //     technicalSkills: [
  //       { name: "React", score: 90 },
  //       { name: "JavaScript", score: 85 },
  //       { name: "TypeScript", score: 75 },
  //       { name: "HTML/CSS", score: 88 },
  //       { name: "Redux", score: 70 }
  //     ],
  //     educationDetails: {
  //       highestDegree: "Bachelor of Technology",
  //       specialization: "Computer Science",
  //       university: "ABC University",
  //       graduationYear: "2018"
  //     },
  //     availabilityDetails: {
  //       interviewAvailability: "Weekdays after 6 PM",
  //       startDate: "June 15, 2025",
  //       relocateConstraints: "None"
  //     },
  //     recommendation: "The candidate has strong relevant experience in frontend development with the required technical skills. Their notice period is reasonable and salary expectations align with our range. Recommend advancing to the technical round.",
  //     redFlags: [
  //       "Frequent job changes in the past (3 companies in 5 years)",
  //       "Some gaps in TypeScript knowledge"
  //     ],
  //     flagDistribution: [
  //       { name: "Green", value: 75 },
  //       { name: "Amber", value: 20 },
  //       { name: "Red", value: 5 }
  //     ]
  //   };
  //   setCandidate(mockCandidate);
  // }

  // Audio player functions
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleProgressClick = (e) => {
    if (audioRef.current) {
      const progressBar = e.currentTarget;
      const clickPosition =
        (e.clientX - progressBar.getBoundingClientRect().left) /
        progressBar.clientWidth;
      const newTime = clickPosition * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Format time from seconds to MM:SS
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Helper function for determining status badge class
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "qualified":
        return "hr-report-status-qualified";
      case "shortlisted":
        return "hr-report-status-shortlisted";
      case "on-hold":
        return "hr-report-status-onhold";
      case "rejected":
        return "hr-report-status-rejected";
      default:
        return "hr-report-status-pending";
    }
  };

  // Helper function for skill progress color
  const getProgressClass = (score) => {
    if (score >= 80) return "hr-report-progress-good";
    if (score >= 70) return "hr-report-progress-average";
    return "hr-report-progress-poor";
  };

  // Flag colors for the pie chart
  const FLAG_COLORS = {
    Green: "#4CAF50",
    Amber: "#FFC107",
    Red: "#F44336",
  };

  // Calculate time markers for audio player
  const timeMarkers = [
    { label: "0:00", time: 0 },
    { label: formatTime(duration / 4), time: duration / 4 },
    { label: formatTime(duration / 2), time: duration / 2 },
    { label: formatTime((3 * duration) / 4), time: (3 * duration) / 4 },
    { label: formatTime(duration), time: duration },
  ];

  const estimatedTimePerQA = Math.floor(duration / (transcript?.length || 1));
  const transcriptWithTimes = (transcript || []).map((item, index) => ({
    ...item,
    timestamp: index * estimatedTimePerQA,
  }));

  return (
    <div className="hr-report-container">
      <div className="hr-report-card">
        {/* Header */}
        <div className="hr-report-header">
          <h1 className="hr-report-header-title">AI Screening Analysis</h1>
          <div className="hr-report-header-actions">
            <button className="ai-report-share-button">
              <span>↗</span>
              Share
            </button>
          </div>
        </div>

        {/* Candidate Info */}
        <div className="hr-report-candidate-info">
          <div className="hr-report-candidate-details">
            <h2 className="hr-report-candidate-name">{safeText(candidate?.name)}</h2>
            <div className="hr-report-candidate-position">
              {safeText(candidate?.position)}
            </div>
            <div
              className={`hr-report-status-badge ${getStatusBadgeClass(
                candidate?.status
              )}`}
            >
              {safeText(candidate.status?.charAt(0).toUpperCase()) +
                safeText(candidate.status?.slice(1))}
            </div>

            <div className="hr-report-details-row">
              <div className="hr-report-detail-item">
                <span>📧</span>
                <span>{safeText(candidate?.email)}</span>
              </div>
              <div className="hr-report-detail-item">
                <span>📱</span>
                <span>{safeText(candidate?.phone)}</span>
              </div>
              <div className="hr-report-detail-item">
                <span>📅</span>
                <span>Screened on: {safeText(candidate?.interviewDate)}</span>
              </div>
            </div>
          </div>

          <div className="hr-report-candidate-score-card">
            <div className="hr-report-score-label">SCREENING SCORE</div>
            <div className="hr-report-score-value">
              {safeText(candidate?.screeningScore)}
            </div>
            <div className="hr-report-progress-bar">
              <div
                className={`hr-report-progress-fill ${getProgressClass(
                  candidate.screeningScore
                )}`}
                style={{ width: `${candidate.screeningScore}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="hr-report-navigation-tabs">
          <div
            className={`hr-report-nav-tab ${
              activeSection === "summary" ? "active" : ""
            }`}
            onClick={() => setActiveSection("summary")}
          >
            Summary
          </div>
          <div
            className={`hr-report-nav-tab ${
              activeSection === "details" ? "active" : ""
            }`}
            onClick={() => setActiveSection("details")}
          >
            Full Details
          </div>
          <div
            className={`hr-report-nav-tab ${
              activeSection === "recording" ? "active" : ""
            }`}
            onClick={() => setActiveSection("recording")}
          >
            Recording
          </div>
        </div>

        {/* Content Area */}
        <div className="hr-report-content-area">
          {activeSection === "summary" && (
            <div className="hr-report-summary-section">
              {/* Recommendation Card */}
              <div className="hr-report-recommendation-card">
                <div className="hr-report-card-title">
                  <span>🏆</span> AI Recommendation
                </div>
                <div className="hr-report-recommendation-content">
                  {safeText(candidate?.recommendation)}
                </div>
              </div>

              {/* Summary Grid */}
              <div className="hr-report-summary-grid">
                {/* Experience & Expectations */}
                <div className="hr-report-summary-card">
                  <div className="hr-report-card-title">
                    <span>👨‍💼</span> Experience & Expectations
                  </div>
                  <div className="hr-report-summary-info-grid">
                    <div className="hr-report-info-item">
                      <div className="hr-report-info-label">
                        Total Experience
                      </div>
                      <div className="hr-report-info-value">
                        {safeText(candidate?.screeningDetails?.totalExperience)}
                      </div>
                    </div>
                    <div className="hr-report-info-item">
                      <div className="hr-report-info-label">
                        Relevant Experience
                      </div>
                      <div className="hr-report-info-value">
                        {safeText(candidate?.screeningDetails?.relevantExperience)}
                      </div>
                    </div>
                    <div className="hr-report-info-item">
                      <div className="hr-report-info-label">
                        Current Company
                      </div>
                      <div className="hr-report-info-value">
                        {safeText(candidate?.screeningDetails?.currentCompany)}
                      </div>
                    </div>
                    <div className="hr-report-info-item">
                      <div className="hr-report-info-label">Current Role</div>
                      <div className="hr-report-info-value">
                        {safeText(candidate?.screeningDetails?.currentRole)}
                      </div>
                    </div>
                    <div className="hr-report-info-item">
                      <div className="hr-report-info-label">Notice Period</div>
                      <div className="hr-report-info-value highlight">
                        {safeText(candidate?.screeningDetails?.noticePeriod)}
                      </div>
                    </div>
                    <div className="hr-report-info-item">
                      <div className="hr-report-info-label">
                        Expected Salary
                      </div>
                      <div className="hr-report-info-value highlight">
                        {safeText(candidate?.screeningDetails?.expectedSalary)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Availability & Preferences */}
                <div className="hr-report-summary-card">
                  <div className="hr-report-card-title">
                    <span>🗓️</span> Availability & Preferences
                  </div>
                  <div className="hr-report-summary-info-list">
                    <div className="hr-report-info-row">
                      <div className="hr-report-info-label">
                        Willing to Relocate
                      </div>
                      <div className="hr-report-info-value">
                        {safeText(candidate?.screeningDetails?.willingToRelocate)}
                      </div>
                    </div>
                    <div className="hr-report-info-row">
                      <div className="hr-report-info-label">
                        Remote Work Preference
                      </div>
                      <div className="hr-report-info-value">
                        {safeText(candidate?.screeningDetails?.remoteWorkPreference)}
                      </div>
                    </div>
                    <div className="hr-report-info-row">
                      <div className="hr-report-info-label">
                        Interview Availability
                      </div>
                      <div className="hr-report-info-value">
                        {safeText(candidate?.availabilityDetails?.interviewAvailability)}
                      </div>
                    </div>
                    <div className="hr-report-info-row">
                      <div className="hr-report-info-label">
                        Earliest Start Date
                      </div>
                      <div className="hr-report-info-value highlight">
                        {safeText(candidate?.availabilityDetails?.startDate)}
                      </div>
                    </div>
                    <div className="hr-report-info-row">
                      <div className="hr-report-info-label">
                        Reason for Change
                      </div>
                      <div className="hr-report-info-value">
                        {safeText(candidate?.screeningDetails?.reasonForChange)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Technical Skills & Flag Analysis Grid */}
              <div className="hr-report-summary-grid">
                {/* Technical Skills */}
                <div className="hr-report-summary-card">
                  <div className="hr-report-card-title">
                    <span>💻</span> Technical Skills
                  </div>
                  <div className="hr-report-skills-container">
                    {candidate?.technicalSkills?.map((skill, index) => (
                      <div key={index} className="hr-report-skill-item">
                        <div className="hr-report-skill-header">
                          <div className="hr-report-skill-name">
                            {safeText(skill.name)}
                          </div>
                          <div className="hr-report-skill-score">
                            {safeText(skill.score)}%
                          </div>
                        </div>
                        <div className="hr-report-progress-bar">
                          <div
                            className={`hr-report-progress-fill ${getProgressClass(
                              skill.score
                            )}`}
                            style={{ width: `${skill.score}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Flag Analysis */}
                <div className="hr-report-summary-card">
                  <div className="hr-report-card-title">
                    <span>🚩</span> Flag Analysis
                  </div>

                  <div className="hr-report-flag-container">
                    <div className="hr-report-flag-chart">
                      <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                          <Pie
                            data={candidate.flagDistribution}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={60}
                            dataKey="value"
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {candidate.flagDistribution?.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={FLAG_COLORS[entry.name]}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="hr-report-red-flags">
                      <h4>Potential Red Flags</h4>
                      <ul>
                        {candidate.redFlags?.map((flag, index) => (
                          <li key={index}>{safeText(flag)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "details" && (
            <div className="hr-report-details-section">
              <div className="hr-report-details-grid">
                {/* Professional Details */}
                <div className="hr-report-details-card">
                  <div className="hr-report-card-title">
                    <span>👨‍💼</span> Professional Details
                  </div>
                  <div className="hr-report-details-content">
                    <div className="hr-report-details-group">
                      <h3>Experience</h3>
                      <div className="hr-report-details-item">
                        <div className="hr-report-details-label">
                          Total Experience
                        </div>
                        <div className="hr-report-details-value">
                          {safeText(candidate?.screeningDetails?.totalExperience)}
                        </div>
                      </div>
                      <div className="hr-report-details-item">
                        <div className="hr-report-details-label">
                          Relevant Experience
                        </div>
                        <div className="hr-report-details-value">
                          {safeText(candidate?.screeningDetails?.relevantExperience)}
                        </div>
                      </div>
                      <div className="hr-report-details-item">
                        <div className="hr-report-details-label">
                          Current Company
                        </div>
                        <div className="hr-report-details-value">
                          {safeText(candidate?.screeningDetails?.currentCompany)}
                        </div>
                      </div>
                      <div className="hr-report-details-item">
                        <div className="hr-report-details-label">
                          Current Role
                        </div>
                        <div className="hr-report-details-value">
                          {safeText(candidate?.screeningDetails?.currentRole)}
                        </div>
                      </div>
                      <div className="hr-report-details-item">
                        <div className="hr-report-details-label">
                          Reason for Change
                        </div>
                        <div className="hr-report-details-value">
                          {safeText(candidate?.screeningDetails?.reasonForChange)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Education Details */}
                <div className="hr-report-details-card">
                  <div className="hr-report-card-title">
                    <span>🎓</span> Education Details
                  </div>
                  <div className="hr-report-details-content">
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        Highest Degree
                      </div>
                      <div className="hr-report-details-value">
                        {candidate.educationDetails?.highestDegree}
                      </div>
                    </div>
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        Specialization
                      </div>
                      <div className="hr-report-details-value">
                        {candidate.educationDetails?.specialization}
                      </div>
                    </div>
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        University/College
                      </div>
                      <div className="hr-report-details-value">
                        {candidate.educationDetails?.university}
                      </div>
                    </div>
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        Year of Graduation
                      </div>
                      <div className="hr-report-details-value">
                        {candidate.educationDetails?.graduationYear}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hr-report-details-grid">
                {/* Joining & Compensation */}
                <div className="hr-report-details-card">
                  <div className="hr-report-card-title">
                    <span>💰</span> Joining & Compensation
                  </div>
                  <div className="hr-report-details-content">
                    <div className="hr-report-details-item highlight-item">
                      <div className="hr-report-details-label">
                        Notice Period
                      </div>
                      <div className="hr-report-details-value">
                        {candidate.screeningDetails?.noticePeriod}
                      </div>
                    </div>
                    <div className="hr-report-details-item highlight-item">
                      <div className="hr-report-details-label">
                        Expected Salary
                      </div>
                      <div className="hr-report-details-value">
                        {candidate.screeningDetails?.expectedSalary}
                      </div>
                    </div>
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        Earliest Start Date
                      </div>
                      <div className="hr-report-details-value">
                        {candidate.availabilityDetails?.startDate}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Work Preferences */}
                <div className="hr-report-details-card">
                  <div className="hr-report-card-title">
                    <span>🔍</span> Work Preferences
                  </div>
                  <div className="hr-report-details-content">
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        Willing to Relocate
                      </div>
                      <div className="hr-report-details-value">
                        {safeText(candidate.screeningDetails?.willingToRelocate)}
                      </div>
                    </div>
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        Remote Work Preference
                      </div>
                      <div className="hr-report-details-value">
                        {safeText(candidate.screeningDetails?.remoteWorkPreference)}
                      </div>
                    </div>
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        Relocation Constraints
                      </div>
                      <div className="hr-report-details-value">
                        {safeText(candidate.availabilityDetails?.relocateConstraints) ||
                          "None"}
                      </div>
                    </div>
                    <div className="hr-report-details-item">
                      <div className="hr-report-details-label">
                        Interview Availability
                      </div>
                      <div className="hr-report-details-value">
                        {safeText(candidate.availabilityDetails?.interviewAvailability)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "recording" && (
            <div className="hr-report-recording-section">
              <div className="hr-report-recording-card">
                <div className="hr-report-card-title">
                  <span>🎙️</span> Screening Call Recording
                </div>

                <div className="hr-report-audio-player">
                  <button
                    className="hr-report-play-button"
                    onClick={togglePlayPause}
                  >
                    {isPlaying ? "⏸" : "▶"}
                  </button>

                  <div className="hr-report-time-display">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>

                  <div className="hr-report-progress-container">
                    <div
                      className="hr-report-audio-progress-bar"
                      onClick={handleProgressClick}
                    >
                      <div
                        className="hr-report-audio-progress"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                      ></div>
                    </div>

                    <div className="hr-report-time-markers">
                      {timeMarkers.map((marker, idx) => (
                        <span key={idx}>{marker.label}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="hr-report-transcript-container">
                  <h3 className="hr-report-transcript-title">
                    Call Transcript
                  </h3>

                  <div className="hr-report-transcript-list">
                    {transcriptWithTimes.length > 0 ? (
                      transcriptWithTimes.map((item, index) => (
                        <div key={index} className="hr-report-transcript-item">
                          <div className="hr-report-transcript-content">
                            <div className="hr-report-transcript-interviewer">
                              <strong>HR:</strong> {item.question}
                            </div>
                            {item.answer && (
                              <div className="hr-report-transcript-candidate">
                                <strong>Candidate:</strong> {item.answer}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="hr-report-transcript-empty">
                        <p>Transcript not available for this screening call.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Hidden audio element */}
                <audio
                  ref={audioRef}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                  src={audioUrl}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Decision Buttons */}
        {/* <div className="hr-report-decision-container">
          <button className="hr-report-decision-button hr-report-reject">
            Reject Candidate
          </button>
          <button className="hr-report-decision-button hr-report-hold">
            Put On Hold
          </button>
          <button className="hr-report-decision-button hr-report-proceed">
            Proceed to Next Round
          </button>
        </div> */}

        {/* Footer */}
        <div className="hr-report-footer">
          Generated by AI HR Screening System • April 10, 2025
        </div>
      </div>
    </div>
  );
};

export default AiCallingReport;
